import { DeleteOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { Button, InputNumber, message, Switch, Upload } from 'antd';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import useSound from 'use-sound';

import { useWarningConfig, WarningConfigApiErrorMap } from 'src/features/warning';
import { ApiError, fcp, passFcpApiErrors } from 'src/services';
import { tusUploadFile } from 'src/services/tus-upload';

/**
 * 音频文件管理组件
 */
const AudioFileManager: React.FC<{ soundFile?: string; onDelete?: () => void; deleteLoading?: boolean }> = ({
  soundFile,
  onDelete,
  deleteLoading,
}) => {
  const [play] = useSound(soundFile);

  const handlePlay = () => {
    play();
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = soundFile;
    link.download = getFileName(soundFile);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!soundFile) return null;

  // 从URL中提取文件名
  const getFileName = (url: string) => {
    const parts = url.split('/');
    const fileName = parts[parts.length - 1];
    return decodeURIComponent(fileName);
  };

  return (
    <FileInfo>
      <FileName onClick={handleDownload}>{getFileName(soundFile)}</FileName>
      <ButtonGroup>
        <Button
          type="text"
          icon={<PlayCircleOutlined />}
          onClick={handlePlay}
          size="small"
          style={{ color: '#1890ff' }}
        />
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={onDelete}
          size="small"
          style={{ color: '#ff4d4f' }}
          loading={deleteLoading}
        />
      </ButtonGroup>
    </FileInfo>
  );
};

const WarningConfigPage: React.FC = () => {
  const [uploading, setUploading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [input, setInput] = useState(0);
  const [{ result: warningConfig }, getWarningConfig] = useWarningConfig();

  useEffect(() => {
    setInput(warningConfig?.warningPrediction || 0);
  }, [warningConfig]);

  const handleAudioUpload = async (file: File) => {
    const isAudio = file.type.startsWith('audio/');
    if (!isAudio) {
      message.error('请上传MP3格式的音频文件');
      return Upload.LIST_IGNORE;
    }

    setUploading(true);
    try {
      const res = await tusUploadFile({
        file,
      });
      await fcp.updateWarningConfig({ body: { soundFile: res.url } }).catch((err: AxiosError<ApiError>) => {
        throw passFcpApiErrors(err.response?.data, WarningConfigApiErrorMap);
      });
      message.success('更新成功');
      getWarningConfig({});
    } catch (error) {
      message.error('上传失败');
    } finally {
      setUploading(false);
    }

    return false;
  };

  const handleSwitch = async (input: boolean) => {
    setSwitchLoading(true);
    await fcp.updateWarningConfig({ body: { enableWarningPrediction: input } }).catch((err: AxiosError<ApiError>) => {
      setSwitchLoading(false);
      throw passFcpApiErrors(err.response?.data, WarningConfigApiErrorMap);
    });
    message.success('更新成功');
    getWarningConfig({});
    setSwitchLoading(false);
  };

  const handleInput = async (input: any) => {
    setInput(input?.target?.value);
    if (input?.target?.value || input?.target?.value === 0) {
      let value = input?.target?.value;
      if (value > 99) {
        value = 99;
      } else if (value < 0) {
        value = 0;
      }
      await fcp
        .updateWarningConfig({ body: { warningPrediction: parseInt(value) } })
        .catch((err: AxiosError<ApiError>) => {
          throw passFcpApiErrors(err.response?.data, WarningConfigApiErrorMap);
        });
      message.success('更新成功');
    }

    getWarningConfig({});
  };

  const handleDeleteSound = async () => {
    setDeleting(true);
    try {
      await fcp.updateWarningConfig({ body: { soundFile: null } }).catch((err: AxiosError<ApiError>) => {
        throw passFcpApiErrors(err.response?.data, WarningConfigApiErrorMap);
      });
      message.success('删除成功');
      getWarningConfig({});
    } catch (error) {
      message.error('删除失败');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container>
      <Top>
        <div className="title">布控报警配置</div>
      </Top>
      <Content>
        <ConfigItem>
          <div className="label">产生报警的卡片有所属执法点时，计算其经过下一个出口匝道的概率：</div>
          <Switch onChange={handleSwitch} checked={warningConfig?.enableWarningPrediction} loading={switchLoading} />
        </ConfigItem>
        <ConfigItem>
          <div className="label">当概率大于</div>
          <InputNumber onBlur={handleInput} value={input} step={1} min={0} max={99} />
          <div className="label right-label"> 时，需要显示在报警卡片上</div>
        </ConfigItem>
        <ConfigItem>
          <div className="label">
            自定义报警声音 <span className="sub-label">(刷新页面后生效)</span> ：
          </div>
          {warningConfig?.soundFile ? (
            <AudioFileManager
              soundFile={warningConfig.soundFile}
              onDelete={handleDeleteSound}
              deleteLoading={deleting}
            />
          ) : (
            <Upload accept="*.mp3" showUploadList={false} beforeUpload={handleAudioUpload}>
              <Button loading={uploading}>上传文件</Button>
            </Upload>
          )}
        </ConfigItem>
      </Content>
    </Container>
  );
};

export default WarningConfigPage;

const Container = styled.div`
  margin: 16px 24px;
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  .title {
    font-size: 1rem;
    font-weight: 600;
    color: rgb(0, 0, 0, 0.88);
    line-height: 1.5;
  }
`;

const Content = styled.div`
  background: #fff;
`;

const ConfigItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;

  .label {
    margin-right: 8px;

    .sub-label {
      font-weight: 300;
      color: rgba(0, 0, 0, 0.45);
    }
  }

  .right-label {
    margin-left: 8px;
  }

  .current-file {
    margin-right: 16px;
    color: rgba(0, 0, 0, 0.45);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 200px;
`;

const FileName = styled.span`
  color: rgb(24, 144, 255);
  font-size: 14px;
  margin-right: 8px;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 4px;
`;
